import { useState, useEffect, useMemo } from 'react';
import { notesAPI } from '@/lib/api';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { History, RotateCcw, Eye, GitCompare, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { diffLines, diffWords } from 'diff';
import { toast } from 'sonner';

export default function NoteHistory({ noteId, onClose }) {
  const { updateNote, selectedNote } = useApp();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareMode, setCompareMode] = useState('view'); // 'view' | 'diff'
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    notesAPI.versions(noteId)
      .then((res) => {
        setVersions(res.data);
        // Auto-select the first version if available
        if (res.data.length > 0) {
          setSelectedVersion(res.data[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [noteId]);

  // Compute diff between selected version and current note
  const diffResult = useMemo(() => {
    if (!selectedVersion || !selectedNote) return null;
    
    const oldContent = selectedVersion.content || '';
    const newContent = selectedNote.content || '';
    
    return {
      lines: diffLines(oldContent, newContent),
      words: diffWords(oldContent, newContent),
      stats: {
        additions: 0,
        deletions: 0,
      }
    };
  }, [selectedVersion, selectedNote]);

  // Calculate diff stats
  const diffStats = useMemo(() => {
    if (!diffResult) return { additions: 0, deletions: 0 };
    
    let additions = 0;
    let deletions = 0;
    
    diffResult.lines.forEach((part) => {
      const lines = part.value.split('\n').length - 1;
      if (part.added) additions += Math.max(1, lines);
      if (part.removed) deletions += Math.max(1, lines);
    });
    
    return { additions, deletions };
  }, [diffResult]);

  const handleRestore = async () => {
    if (!selectedVersion) return;
    
    setRestoring(true);
    try {
      await updateNote(noteId, {
        title: selectedVersion.title,
        content: selectedVersion.content,
      });
      toast.success('Note restored to selected version');
      onClose();
    } catch (err) {
      toast.error('Failed to restore version');
    }
    setRestoring(false);
  };

  const selectedIndex = versions.findIndex((v) => v.id === selectedVersion?.id);
  const canGoPrev = selectedIndex < versions.length - 1;
  const canGoNext = selectedIndex > 0;

  const navigateVersion = (direction) => {
    if (direction === 'prev' && canGoPrev) {
      setSelectedVersion(versions[selectedIndex + 1]);
    } else if (direction === 'next' && canGoNext) {
      setSelectedVersion(versions[selectedIndex - 1]);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh]" data-testid="note-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Version History
          </DialogTitle>
          <DialogDescription>
            View and restore previous versions of this note. Versions are saved automatically when you edit.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 min-h-[400px]">
          {/* Version List Sidebar */}
          <ScrollArea className="w-52 flex-shrink-0 border-r border-border pr-3">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-5 h-5 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No previous versions</p>
                <p className="text-xs mt-1 opacity-70">Versions are created when you edit</p>
              </div>
            ) : (
              <div className="space-y-1">
                {versions.map((v, idx) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVersion(v)}
                    data-testid={`version-item-${v.id}`}
                    className={`w-full text-left p-2.5 rounded-md text-xs transition-all ${
                      selectedVersion?.id === v.id 
                        ? 'bg-primary/10 text-foreground border border-primary/30' 
                        : 'text-muted-foreground hover:bg-muted/50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <History className="w-3 h-3" />
                        <span className="font-medium">{format(new Date(v.created_at), 'MMM d, HH:mm')}</span>
                      </div>
                      {idx === 0 && (
                        <span className="text-[9px] bg-muted px-1.5 py-0.5 rounded">Latest</span>
                      )}
                    </div>
                    <p className="truncate mt-1 text-[10px] opacity-70">{v.title || 'Untitled'}</p>
                    <p className="text-[9px] opacity-50 mt-0.5">
                      {formatDistanceToNow(new Date(v.created_at), { addSuffix: true })}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Content Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {selectedVersion ? (
              <>
                {/* Header with navigation and actions */}
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => navigateVersion('prev')}
                      disabled={!canGoPrev}
                      title="Older version"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Version {versions.length - selectedIndex} of {versions.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => navigateVersion('next')}
                      disabled={!canGoNext}
                      title="Newer version"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Tabs value={compareMode} onValueChange={setCompareMode} className="h-8">
                      <TabsList className="h-7">
                        <TabsTrigger value="view" className="h-6 text-xs px-2 gap-1">
                          <Eye className="w-3 h-3" /> View
                        </TabsTrigger>
                        <TabsTrigger value="diff" className="h-6 text-xs px-2 gap-1">
                          <GitCompare className="w-3 h-3" /> Diff
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>

                    <Button
                      size="sm"
                      onClick={handleRestore}
                      disabled={restoring}
                      className="h-7 text-xs"
                      data-testid="restore-version-btn"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      {restoring ? 'Restoring...' : 'Restore'}
                    </Button>
                  </div>
                </div>

                {/* Version Content */}
                <div className="flex-1 overflow-auto">
                  {compareMode === 'view' ? (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">{selectedVersion.title || 'Untitled'}</h3>
                      <ScrollArea className="h-[300px]">
                        <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap bg-secondary/30 p-4 rounded-md border border-border leading-relaxed">
                          {selectedVersion.content || '(empty)'}
                        </pre>
                      </ScrollArea>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold">Changes from this version to current</h3>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-green-500">+{diffStats.additions} additions</span>
                          <span className="text-red-400">-{diffStats.deletions} deletions</span>
                        </div>
                      </div>
                      <ScrollArea className="h-[300px]">
                        <div className="font-mono text-xs bg-secondary/30 p-4 rounded-md border border-border leading-relaxed">
                          {diffResult?.lines.map((part, index) => (
                            <span
                              key={index}
                              className={`${
                                part.added 
                                  ? 'bg-green-500/20 text-green-300' 
                                  : part.removed 
                                    ? 'bg-red-500/20 text-red-300 line-through' 
                                    : 'text-muted-foreground'
                              }`}
                            >
                              {part.value}
                            </span>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                <div className="text-center">
                  <History className="w-10 h-10 mx-auto mb-2 opacity-20" />
                  <p>Select a version to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
